<template>
  <div class="routing-forms">

    <!-- === KPI SUMMARY CARDS === -->
    <div class="kpi-row fade-in">
      <div class="kpi-card" v-for="(kpi, i) in formStats" :key="i">
        <div>
          <h4>{{ kpi.title }}</h4>
          <div class="value">
            <span v-if="!isLoading">{{ kpi.value }}</span>
            <span v-else class="loading-dot"></span>
          </div>
        </div>
        <i :class="kpi.icon + ' kpi-icon'"></i>
      </div>
    </div>

    <!-- === FILTERS + ADD NEW FORM BUTTON === -->
    <div class="grid-header fade-in">
      <div class="filters">
        <div class="search-box">
          <i class="bx bx-search"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search forms..."
            @input="fetchForms"
          />
        </div>

        <select v-model="selectedStatus" @change="fetchForms">
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <button class="btn-primary" @click="addNewForm">
        <i class="bx bx-plus"></i> New Form
      </button>
    </div>

    <!-- === LOADER === -->
   <transition name="fade-slide" v-if="isLoading" >
      <div class="global-loader">
        <div class="loader-spinner"></div>
        <p>Loading...</p>
      </div>
    </transition>

    <!-- === FORMS GRID === -->
    <div v-else class="website-grid fade-in">
      <div
        class="website-card"
        v-for="(form, i) in filteredForms"
        :key="i"
      >
        <!-- Header -->
        <div class="website-card-header">
          <h4><i class="bx bx-news"></i> {{ form.title }}</h4>

          <!-- Card Menu -->
          <div class="card-menu">
            <i class="bx bx-dots-vertical-rounded"></i>
            <div class="card-menu-dropdown">
              <button title="Edit Form" @click="editForm(form)">
                <i class="bx bx-edit"></i> Edit
              </button>
              <button title="Delete Form" @click="deleteForm(form)">
                <i class="bx bx-trash"></i> Delete
              </button>
            </div>
          </div>
        </div>

        <!-- Body -->
        <div class="website-card-body">
          <div class="form-preview">
            <img
              :src="form.image"
              alt="form preview"
              onerror="this.src='https://via.placeholder.com/400x180.png?text=Form+Preview';"
            />
          </div>

          <div class="form-meta">
            <div><i class="bx bx-list-check"></i> {{ form.fields.length }} Fields</div>
            <div>
              <i
                class="bx bx-check-circle"
                :class="form.status === 'Active' ? 'text-green' : 'text-gray'"
              ></i>
              {{ form.status }}
            </div>
            <div><i class="bx bx-network-chart"></i> {{ form.pagesInUse }} Booking Pages Linked</div>
          </div>

          <div class="fields-list">
            <span
              v-for="(field, index) in form.fields"
              :key="index"
              class="field-tag"
            >
              {{ field.title }}
            </span>
          </div>
        </div>

        <!-- Footer -->
        <div class="website-card-footer">
          <span class="website-domain">{{ form.createdAt }}</span>
          <div class="footer-actions">
            <button class="btn-edit" @click="editForm(form)">
              <i class="bx bx-edit"></i> Edit
            </button>
            <button class="btn-delete" @click="deleteForm(form)">
              <i class="bx bx-trash"></i> Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-if="!isLoading && filteredForms.length === 0"
        class="empty-state fade-in"
      >
        <i class="bx bx-file-blank"></i>
        <p>No forms found. Create your first one!</p>
        <button
  class="btn-primary"
  @click="addNewForm"
  style="
    min-width: 140px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 8px;
    padding: 6px 14px;
  "
>
  <i class="bx bx-plus" style="font-size: 16px; color: #fff; margin-top: .6rem;"></i>
  Create Form
</button>

      </div>
    </div>

    <!-- === PAGINATION === -->
    <div class="pagination fade-in" v-if="forms.length > 0">
      <span>Showing 1–{{ filteredForms.length }} of {{ forms.length }} forms</span>
      <div class="pagination-buttons">
        <button><i class="bx bx-chevron-left"></i></button>
        <button class="active">1</button>
        <button>2</button>
        <button>3</button>
        <button><i class="bx bx-chevron-right"></i></button>
      </div>
    </div>

    <br /><br />
     <ConfirmDialog
  :show="showConfirm"
  :title="confirmTitle"
  :message="confirmMessage"
  @cancel="showConfirm = false"
  @confirmAction="handleConfirm"
/>
  </div>
</template>


<script>
import { getForms, getFormReport, deleteForm } from "@/apis/formapis";
import { useToast } from "vue-toastification";
import ConfirmDialog from "@/comps/ConfirmDialog.vue";


export default {
  name: "RoutingForms",
  components:{
    ConfirmDialog
  },
  data() {
    return {
      showConfirm:false,
      confirmTitle:"",
      confirmMessage:"",
      searchQuery: "",
      selectedStatus: "",
      formStats: [
        { title: "All Forms", value: "0", icon: "bx bx-news" },
        { title: "Active", value: "0", icon: "bx bx-check-circle" },
        { title: "Inactive", value: "0", icon: "bx bx-x-circle" }
      ],
      forms: [],
      isLoading: false,
      page_no: 0,
    };
  },
  computed: {
    filteredForms() {
      return this.forms.filter(
        (f) =>
          f.title.toLowerCase().includes(this.searchQuery.toLowerCase()) &&
          (!this.selectedStatus || f.status === this.selectedStatus)
      );
    },
  },
  mounted() {
    this.fetchFormReport();
    this.fetchForms();
  },
  methods: {
    confirmCallback() {},
    openConfirm(title, message, onConfirm){
      this.confirmTitle = title;
      this.confirmMessage = message;
      this.confirmCallback = onConfirm;
      this.showConfirm = true;
    },
    handleConfirm(){
      this.showConfirm = false;
      this.confirmCallback();
    },
    async fetchFormReport() {
      const toast = useToast();
      const ldata = JSON.parse(sessionStorage.getItem("ldata"));
      try {
        const res = await getFormReport(ldata?.user_id);
        if (res.status) {
          this.formStats = [
            { title: "All Forms", value: res.all_forms || 0, icon: "bx bx-news" },
            { title: "Active", value: res.active_forms || 0, icon: "bx bx-check-circle" },
            { title: "Inactive", value: res.inactive_forms || 0, icon: "bx bx-x-circle" },
          ];
        } else {
          toast.error(res.success || "Failed to load form stats");
        }
      } catch (err) {
        console.error("fetchFormReport error:", err);
        toast.error("Error loading form report");
      }
    },

    async fetchForms() {
      const toast = useToast();
      const ldata = JSON.parse(sessionStorage.getItem("ldata"));
      this.isLoading = true;
      try {
        const res = await getForms(
          ldata?.user_id,
          this.page_no,
          this.selectedStatus,
          this.searchQuery
        );

        if (res.status && res.records?.length) {
          this.forms = res.records.map((f) => ({
            id: f.form_temp_id,
            title: f.form_temp_title,
            desc: f.form_temp_desc,
            image: f.form_temp_image || "https://via.placeholder.com/400x180.png?text=Form",
            fields: f.form_temp_fields.length>0
              ? JSON.parse(f.form_temp_fields)
              : [],
            status: f.form_temp_status,
            pagesInUse: f.pagesInUse || 0,
            createdAt: new Date(f.form_temp_datetime).toLocaleDateString(),
          }));
        } else {
          this.forms = [];
          toast.success("No forms found");
        }
      } catch (err) {
        console.error("fetchForms error:", err);
        toast.error("Error fetching forms");
      } finally {
        this.isLoading = false;
      }
    },

    addNewForm() {
      this.$router.push("/new_form");
    },

    editForm(form) {
      sessionStorage.setItem("form_data", JSON.stringify(form));
      this.$router.push({ path: "/new_form", query: { id: form.id } });
    },

    async deleteForm(form) {
      const toast = useToast();
      this.openConfirm(
        "Delete Item",
        `Are you sure you want to delete?`,
        async () => {
          try {
            const res = await deleteForm(form.id);
            if (res.status) {
              toast.success(res.success || "Form deleted successfully");
              this.fetchForms();
              this.fetchFormReport();
            } else {
              toast.error(res.success || "Failed to delete form");
            }
          } catch (err) {
            console.error("deleteForm error:", err);
            toast.error("Error deleting form");
          }
        }
      );
    },
  },
};
</script>


<style scoped>
/* === Fade Animations === */
.fade-in {
  animation: fadeInUp 0.6s ease forwards;
  opacity: 0;
  transform: translateY(12px);
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* === Filters === */
.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.filters select {
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 13px;
}

.form-preview {
    width: 100%;
  height: 140px;
    border-radius: 8px;
  margin-bottom: 12px;
    background-color: #ddd;
}

/* === Form Preview Image === */
.form-preview img {
  width: 100%;
  height: 140px;
  
  object-fit: cover;
  border-radius: 8px;
}

/* === Meta & Fields === */
.form-meta {
  display: flex;
  justify-content: space-between;
  color: var(--muted);
  font-size: 13px;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}
.form-meta i {
  color: var(--primary);
  margin-right: 5px;
}
.fields-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.field-tag {
  background: #f3f4f6;
  color: var(--text);
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 6px;
}

/* === Footer Buttons === */
.footer-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.btn-edit,
.btn-delete {
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.btn-edit {
  background: var(--primary);
  color: #fff;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.2);
}
.btn-edit:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
}
.btn-delete {
  background: #f3f4f6;
  color: var(--text);
}
.btn-delete:hover {
  background: #fee2e2;
  color: #991b1b;
  transform: translateY(-1px);
}

/* === Dropdown Menu === */
.card-menu {
  position: relative;
}
.card-menu i {
  cursor: pointer;
  font-size: 1.2rem;
}
.card-menu-dropdown {
  position: absolute;
  right: 0;
  top: 1.5rem;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: none;
  flex-direction: column;
  z-index: 10;
}
.card-menu:hover .card-menu-dropdown {
  display: flex;
}
.card-menu-dropdown button {
  border: none;
  background: none;
  text-align: left;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.25s ease;
}
.card-menu-dropdown button:hover {
  background: #f3f4f6;
}

/* === Pagination === */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}
.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}
.pagination-buttons button {
  border: none;
  background: #f3f4f6;
  color: var(--text);
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}
.pagination-buttons button.active {
  background: var(--primary);
  color: #fff;
}
.pagination-buttons button:hover {
  background: var(--primary);
  color: #fff;
}

/* ==========================================================
   📱 RESPONSIVE FIXES FOR ROUTING FORMS COMPONENT
   ========================================================== */

/* === KPI CARDS GRID === */
@media (max-width: 992px) {
  .kpi-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .kpi-card {
    flex: 1 1 calc(50% - 16px);
    min-width: 240px;
  }
}

@media (max-width: 600px) {
  .kpi-card {
    flex: 1 1 100%;
  }
}

/* === FILTERS + HEADER SECTION === */
@media (max-width: 768px) {
  .grid-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .filters {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .filters select,
  .filters .search-box {
    width: 100%;
  }

  .filters .search-box {
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 6px 10px;
    gap: 8px;
  }

  .filters .search-box input {
    border: none;
    outline: none;
    flex: 1;
    font-size: 13px;
    color: var(--text);
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }
}

/* === WEBSITE GRID / FORM CARDS === */
@media (max-width: 992px) {
  .website-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
}

@media (max-width: 600px) {
  .website-grid {
    grid-template-columns: 1fr;
  }

  .website-card {
    padding: 1rem;
  }

  .website-card-header h4 {
    font-size: 1rem;
  }

  .form-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .fields-list {
    flex-wrap: wrap;
    gap: 5px;
  }

  .field-tag {
    font-size: 11px;
    padding: 3px 7px;
  }

  .website-card-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .btn-edit,
  .btn-delete {
    flex: 1 1 48%;
    text-align: center;
  }

  .footer-actions {
    width: 100%;
    display: flex;
    gap: 10px;
  }
}

/* === PAGINATION === */
@media (max-width: 600px) {
  .pagination {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .pagination-buttons {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }
}

/* === SMALL SCREEN OPTIMIZATION === */
@media (max-width: 480px) {
  .routing-forms {
    padding: 0 0.8rem;
  }

  .kpi-card h4 {
    font-size: 0.9rem;
  }

  .kpi-card .value {
    font-size: 1.2rem;
  }

  .filters {
    gap: 8px;
  }

  .btn-primary {
    padding: 8px 12px;
    font-size: 13px;
  }

  .form-preview img {
    height: 120px;
  }

  .form-meta {
    gap: 4px;
    font-size: 12px;
  }

  .field-tag {
    font-size: 11px;
    padding: 3px 6px;
  }
}

/* === Loader Overlay === */
.loader-overlay {
  position: relative;
  width: 100%;
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.loader {
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary);
  border-radius: 50%;
  width: 38px;
  height: 38px;
  animation: spin 0.8s linear infinite;
  margin-bottom: 10px;
}
.loader-overlay p {
  color: var(--muted);
  font-size: 14px;
  margin-top: 4px;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* === KPI Loading Dot (Animated Value Placeholder) === */
.loading-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary);
  animation: dotPulse 0.8s ease-in-out infinite;
}
@keyframes dotPulse {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.4); }
}

/* === Empty State (No Records Found) === */
.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 0;
  color: var(--muted);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.empty-state i {
  font-size: 3rem;
  color: var(--primary);
  margin-bottom: 10px;
}
.empty-state p {
  font-size: 14px;
  margin-bottom: 14px;
  color: var(--text);
}
.empty-state .btn-primary {
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.empty-state .btn-primary:hover {
  background: #1e3a8a;
  transform: translateY(-1px);
}

/* === Status Icon Colors === */
.text-green {
  color: #16a34a;
}
.text-gray {
  color: #9ca3af;
}


</style>
